import { useState, useRef, useEffect } from 'react';
import { useLanguageStore } from '../store/languageStore';
import { useTranslation } from '../hooks/useTranslation';
import { availableLanguages, languageNames, type Language } from '../i18n';

const LanguageSelector = () => {
  const { language, setLanguage } = useLanguageStore();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Map available languages to options with translated labels
  const languageOptions = availableLanguages.map(lang => ({
    value: lang,
    label: t(`profile.${getLanguageKey(lang)}`),
    nativeName: languageNames[lang],
  }));

  const currentLanguage = languageOptions.find(lang => lang.value === language) || languageOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-end w-full px-4 py-3 bg-tg-secondary rounded-lg text-sm font-semibold text-white hover:bg-[#2a2a2a] transition-colors"
      >
        <span>{currentLanguage.nativeName}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-tg-secondary rounded-lg overflow-hidden border border-gray-800 shadow-xl z-[10000] min-w-full max-h-[280px] overflow-y-auto">
          {languageOptions.map((lang) => (
            <button
              key={lang.value}
              onClick={() => {
                setLanguage(lang.value as Language);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left text-sm font-semibold transition-colors ${
                language === lang.value
                  ? 'bg-primary text-black'
                  : 'text-white hover:bg-[#2a2a2a]'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{lang.nativeName}</span>
                {language === lang.value && (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to get language key for translation
function getLanguageKey(lang: Language): string {
  const keyMap: Record<Language, string> = {
    en: 'english',
    vi: 'vietnamese',
    es: 'spanish',
    pt: 'portuguese',
    zh: 'chinese',
    id: 'indonesian',
  };
  return keyMap[lang];
}

export default LanguageSelector;

